import { FunctionComponent, useMemo } from 'react';
import { Button, Flex, FormControl, FormLabel } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SelectField } from '@/components/SelectField';
import { GetParamsObject } from '@/model/query-params';
import { Stage, StageType } from '@/model';
import { useChampionships } from '@/services/queries/championship';
import { useSeasons } from '@/services/queries/season';
import { useStages } from '@/services/queries/stage';
import { StandingsFilters } from '../../types';

const PREFIX = 'pages.application_championship_stage_standings.filter_box';

type StandingsFilterBoxProps = {
  filters: StandingsFilters;
  onChange: (next: StandingsFilters) => void;
  onClear: () => void;
};

export const StandingsFilterBox: FunctionComponent<StandingsFilterBoxProps> = ({
  filters,
  onChange,
  onClear,
}) => {
  const { t } = useTranslation();

  const championshipsParams = useMemo(
    () => GetParamsObject({} as Record<string, string>, 1, 200, 'asc', 'name'),
    [],
  );
  const { data: championshipsData } = useChampionships(championshipsParams);

  const seasonsParams = useMemo(
    () =>
      filters.championship_uuid
        ? GetParamsObject(
            { championship_uuid: filters.championship_uuid } as Record<string, string>,
            1,
            200,
            'desc',
            'year',
          )
        : undefined,
    [filters.championship_uuid],
  );
  const { data: seasonsData } = useSeasons(seasonsParams);

  const stagesParams = useMemo(
    () =>
      filters.season_uuid
        ? GetParamsObject(
            { season_uuid: filters.season_uuid } as Record<string, string>,
            1,
            200,
            'asc',
            'stage_order',
          )
        : undefined,
    [filters.season_uuid],
  );
  const { data: stagesData } = useStages(stagesParams);

  const handleChampionshipChange = (value: string) => {
    onChange({ championship_uuid: value, season_uuid: '', stage_uuid: '' });
  };

  const handleSeasonChange = (value: string) => {
    onChange({ ...filters, season_uuid: value, stage_uuid: '' });
  };

  const handleStageChange = (value: string) => {
    onChange({ ...filters, stage_uuid: value });
  };

  const labelForStage = (stage: Stage): string => {
    const baseName = stage.group_name ? `${stage.name} · ${stage.group_name}` : stage.name;
    if (stage.type === StageType.KNOCKOUT) {
      return `${baseName} ${t(`${PREFIX}.stage_knockout_suffix`)}`;
    }
    return baseName;
  };

  return (
    <Flex direction="column" gap={4}>
      <Flex direction={{ base: 'column', md: 'row' }} gap={4}>
        <FormControl>
          <FormLabel fontSize="sm" color="gray.600">
            {t(`${PREFIX}.championship_label`)}
          </FormLabel>
          <SelectField
            value={filters.championship_uuid}
            onChange={(event) => handleChampionshipChange(event.target.value)}
          >
            <option value="">{t(`${PREFIX}.placeholder_select`)}</option>
            {(championshipsData?.data ?? []).map((champ) => (
              <option key={champ.uuid} value={champ.uuid}>
                {champ.name}
              </option>
            ))}
          </SelectField>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color="gray.600">
            {t(`${PREFIX}.season_label`)}
          </FormLabel>
          <SelectField
            value={filters.season_uuid}
            isDisabled={!filters.championship_uuid}
            onChange={(event) => handleSeasonChange(event.target.value)}
          >
            <option value="">{t(`${PREFIX}.placeholder_select`)}</option>
            {(seasonsData?.data ?? []).map((season) => (
              <option key={season.uuid} value={season.uuid}>
                {season.year}
              </option>
            ))}
          </SelectField>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color="gray.600">
            {t(`${PREFIX}.stage_label`)}
          </FormLabel>
          <SelectField
            value={filters.stage_uuid}
            isDisabled={!filters.season_uuid}
            onChange={(event) => handleStageChange(event.target.value)}
          >
            <option value="">{t(`${PREFIX}.placeholder_select`)}</option>
            {(stagesData?.data ?? []).map((stage) => (
              <option key={stage.uuid} value={stage.uuid}>
                {labelForStage(stage)}
              </option>
            ))}
          </SelectField>
        </FormControl>
      </Flex>

      <Flex justify="flex-end">
        <Button variant="outline" size="sm" onClick={onClear}>
          {t(`${PREFIX}.clear_filters`)}
        </Button>
      </Flex>
    </Flex>
  );
};
